import { Pagination } from "@mui/material";
import {
  BackpackIcon,
  CalendarIcon,
  Cross2Icon,
  HomeIcon,
  PersonIcon,
} from "@radix-ui/react-icons";
import {
  CheckCircle2,
  Loader2,
  Package,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Pbo } from "../../components/Widget/Pbo";
import { Periode } from "../../components/Widget/Periode";
import { find, store } from "../../services/service";
import { useEquipeStore } from "../../store/EquipeStore";
import { useStoreApp } from "../../store/storeApp";
import { useStoreSurvey } from "../../store/SurveyStore";
import {
  ATTRIBUTION_RDV_CORBEILLE_PRESTATAIRE,
  LISTE_COMMUNE_SANS_PAGINATION,
  LISTE_EQUIPE_PRESTATAIRE,
  LISTE_PRESTATAIRE,
  RENDEZ_VOUS_EN_ATTENTE_ATTRIBUTION,
  RETIRER_DOSSIER_PRESTATAIRE,
} from "../../Utils/constant";

export const PrestataireAttribution = () => {
  const prestataire = useStoreApp();
  const dataListe = useStoreSurvey();
  const [commune, setCommune] = useState([]);
  const [message, setMessage] = useState("");
  const [item, setItem] = useState(null);
  const [param, setParam] = useState({
    prestataireId: "",
    communeId: "",
    equipeId: "",
  });
  const [communeId, setCommuneId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPrestataireDossiers, setSelectedPrestataireDossiers] =
    useState([]);
  const [loadingDossiers, setLoadingDossiers] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [dossierPage, setDossierPage] = useState(0);
  const [dossierSize] = useState(10);
  const [dossierTotalPages, setDossierTotalPages] = useState(1);
  const [msg, setMsg] = useState("");
  const [assignModalItem, setAssignModalItem] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedTeamPrestataire, setSelectedTeamPrestataire] = useState("");
  const equipe = useEquipeStore();

  // Nouveaux états pour la sélection et le retrait
  const [selectedDossiers, setSelectedDossiers] = useState([]);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const getCommuneByPrestataire = async (prestataireId) => {
    try {
      const res = await find(
        `${LISTE_COMMUNE_SANS_PAGINATION}?prestataireId=${prestataireId}`,
      );
      setCommune(res?.data ?? []);
    } catch (err) {
      toast.error("Erreur lors du chargement des communes");
    }
  };

  // Chargement initial des communes
  useEffect(() => {
    getCommuneByPrestataire(param.prestataireId);
  }, [param.prestataireId]);

  // Chargement des prestataires selon la commune sélectionnée
  useEffect(() => {
    loadPrestataires(0, searchTerm);
    setCurrentPage(0);
  }, [communeId]);

  // Fonction de chargement des prestataires
  const loadPrestataires = async (page = 0, search = "") => {
    try {
      await prestataire.getData(
        LISTE_PRESTATAIRE,
        { page, param: search, size: 6 },
        `&communeId=${communeId}`,
      );
    } catch (error) {
      console.error("Erreur lors du chargement des prestataires:", error);
      toast.error("Erreur lors du chargement des prestataires");
    }
  };

  // Gestion de la recherche avec indicateur de chargement
  const handleSearch = async (value) => {
    setSearchTerm(value);
    setIsSearching(true);
    setCurrentPage(0);
    try {
      await prestataire.getData(
        LISTE_PRESTATAIRE,
        { page: 0, param: value, size: 6 },
        `&communeId=${communeId}`,
      );
    } finally {
      setIsSearching(false);
    }
  };

  // Gestion du changement de commune
  const handleCommuneChange = (value) => {
    setCommuneId(value);
    setSearchTerm("");
    setItem(null);
  };

  // Pagination
  const handlePageChange = async (page) => {
    setCurrentPage(page - 1);
    await prestataire.getData(
      LISTE_PRESTATAIRE,
      { page: page - 1, param: searchTerm, size: 6 },
      `&communeId=${communeId}`,
    );
  };

  // Charger dossiers pour le prestataire sélectionné (avec pagination)
  const fetchDossiers = async (page = 0, param) => {
    if (!item) {
      setSelectedPrestataireDossiers([]);
      setDossierTotalPages(1);
      setDossierPage(0);
      return;
    }
    setLoadingDossiers(true);
    try {
      const res = await find(
        `${ATTRIBUTION_RDV_CORBEILLE_PRESTATAIRE}/${item.id}?page=${page}&param=${param ?? ""}&size=${dossierSize}`,
      );
      const payload = res?.data ?? {};
      const list = payload.data ?? payload.donnee ?? [];
      setSelectedPrestataireDossiers(list);
      setDossierTotalPages(payload.totalPages ?? 1);
      setDossierPage(page);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.donnee || "Erreur chargement dossiers");
      setSelectedPrestataireDossiers([]);
      setDossierTotalPages(1);
      setDossierPage(0);
    } finally {
      setLoadingDossiers(false);
    }
  };

  useEffect(() => {
    if (!item) return;
    equipe.getEquipePrestataire(LISTE_EQUIPE_PRESTATAIRE + "/" + item?.id);
  }, [item]);

  useEffect(() => {
    // reset when prestataire selection changes
    if (!item) {
      setSelectedPrestataireDossiers([]);
      setDossierTotalPages(1);
      setDossierPage(0);
      setSelectedDossiers([]);
      return;
    }
    fetchDossiers(0);
  }, [item]);

  const handleAssignTeamPrestataire = async () => {
    if (!assignModalItem || !selectedTeamPrestataire) return;

    setIsAssigning(true);
    setMsg("");

    try {
      const res = await store(
        `/commerciale/rdv-corbeille/v3/${assignModalItem.abonnement?.id}/attribuer-equipe/${selectedTeamPrestataire}`,
        "application/json",
        {},
      );

      if (res.status === 200) {
        fetchDossiers(dossierPage);
        document.getElementById("assign-modal-equipe").close();
        setAssignModalItem(null);
        setSelectedTeamPrestataire("");
        toast.success("Prestataire affecté avec succès");
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.donnee || "Erreur lors de l'attribution";
      setMsg(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsAssigning(false);
    }
  };

  // Gestion de la sélection des dossiers
  const handleSelectDossier = (dossierId) => {
    setSelectedDossiers((prev) => {
      if (prev.includes(dossierId)) {
        return prev.filter((id) => id !== dossierId);
      } else {
        return [...prev, dossierId];
      }
    });
  };

  // Sélectionner/désélectionner tous les dossiers
  const handleSelectAll = () => {
    if (selectedDossiers.length === selectedPrestataireDossiers.length) {
      setSelectedDossiers([]);
    } else {
      setSelectedDossiers(
        selectedPrestataireDossiers.map((d) => d.abonnement?.id),
      );
    }
  };

  // Fonction pour retirer les dossiers sélectionnés
  const handleRemoveDossiers = async () => {
    if (selectedDossiers.length === 0) return;
    setIsRemoving(true);
    try {
      store(RETIRER_DOSSIER_PRESTATAIRE, "application/json", {
        abonnementIds: selectedDossiers,
      }).then(() => {
        toast.success(
          `${selectedDossiers.length} dossier(s) retiré(s) avec succès`,
        );
        setSelectedDossiers([]);
        setShowRemoveModal(false);
        fetchDossiers(dossierPage);
        dataListe.getData(
          `${RENDEZ_VOUS_EN_ATTENTE_ATTRIBUTION}?page=0&param=&size=8&communeId=`,
        );
      });
    } catch (err) {
      const errorMsg =
        err?.response?.data?.donnee || "Erreur lors du retrait des dossiers";
      toast.error(errorMsg);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className="mt-4">
      <div className="my-4 flex flex-col md:flex-row items-stretch md:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <input
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            type="text"
            placeholder="Rechercher un prestataire par nom..."
            className="input input-bordered px-3 pl-10 pr-10 w-full h-11 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
            disabled={prestataire?.loading}
          />
          <Search className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
          {isSearching && (
            <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-3.5 text-amber-500" />
          )}
          {searchTerm && !isSearching && (
            <button
              onClick={() => handleSearch("")}
              className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <select
          className="select select-bordered h-11 text-sm min-w-[200px] focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          value={communeId}
          onChange={(e) => handleCommuneChange(e.target.value)}
          disabled={commune?.loading}
        >
          <option value="">
            {commune?.loading ? "Chargement..." : "Toutes les communes"}
          </option>
          {commune?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.designation}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Liste des prestataires */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-500" />
              Prestataires
            </h3>

            {prestataire?.loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500 mb-3" />
                <p className="text-sm text-gray-600">Chargement...</p>
              </div>
            ) : prestataire?.data?.data?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="bg-gray-100 rounded-full p-4 mb-3">
                  <BackpackIcon className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-sm text-gray-600 font-medium mb-1">
                  Aucun prestataire
                </p>
                <p className="text-xs text-gray-400 text-center px-4">
                  {searchTerm
                    ? "Essayez une autre recherche"
                    : "Aucun prestataire disponible"}
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                  {prestataire?.data?.data?.map((prest) => (
                    <div
                      key={prest.id}
                      onClick={() => setItem(prest)}
                      className={`group relative flex items-center gap-3 cursor-pointer border rounded-lg p-3 transition-all duration-200 ${
                        item?.id === prest.id
                          ? "border-amber-400 bg-amber-50 shadow-sm"
                          : "border-gray-200 bg-white hover:border-amber-300 hover:bg-amber-50/50"
                      }`}
                    >
                      <div
                        className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                          item?.id === prest.id
                            ? "bg-amber-200"
                            : "bg-amber-100 group-hover:bg-amber-200"
                        }`}
                      >
                        <BackpackIcon
                          className={`w-5 h-5 ${
                            item?.id === prest.id
                              ? "text-amber-700"
                              : "text-amber-600"
                          }`}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {prest?.designation}
                        </p>
                        {prest?.commune && (
                          <p className="text-xs text-gray-500 truncate">
                            {prest.commune}
                          </p>
                        )}
                      </div>

                      {item?.id === prest.id && (
                        <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      )}

                      {prest?.actif === false && (
                        <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full" />
                      )}
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {prestataire?.data?.totalPages > 1 && (
                  <div className="mt-4 pt-4 border-t border-gray-100 flex justify-center">
                    <Pagination
                      page={currentPage + 1}
                      onChange={(event, newValue) => handlePageChange(newValue)}
                      count={prestataire?.data?.totalPages}
                      size="small"
                      variant="outlined"
                      color="primary"
                      shape="rounded"
                      disabled={prestataire?.loading}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <dialog id="assign-modal-equipe" className="modal">
          <div className="modal-box w-96">
            <h3 className="font-bold text-lg">Affecter à une equipe</h3>
            <p className="text-xs text-gray-500 mb-3 ">
              Sélectionnez une equipe à affecter pour ce rendez-vous.
            </p>
            {msg && <p className="text-red-500 text-sm mb-2">{msg}</p>}
            <div className="mb-3">
              {equipe?.equipePrestataire.length === 0 ? (
                <p className="text-sm text-gray-500 my-8">
                  Aucune equipe disponible pour ce prestataire.
                </p>
              ) : (
                <select
                  value={selectedTeamPrestataire}
                  onChange={(e) => setSelectedTeamPrestataire(e.target.value)}
                  className="select select-bordered w-full"
                  disabled={isAssigning}
                >
                  <option value="">-- Choisir une equipe --</option>
                  {equipe?.equipePrestataire?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.designation}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="modal-action">
              <button
                onClick={handleAssignTeamPrestataire}
                disabled={isAssigning || !selectedTeamPrestataire}
                className={`btn btn-sm ${
                  isAssigning || !selectedTeamPrestataire
                    ? "btn-disabled opacity-50"
                    : "bg-main text-black border-main"
                }`}
              >
                {isAssigning ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Affectation...
                  </>
                ) : (
                  "Affecter"
                )}
              </button>
              <button
                onClick={() => {
                  document.getElementById("assign-modal-equipe").close();
                  setAssignModalItem(null);
                  setMsg("");
                }}
                disabled={isAssigning}
                className="btn btn-sm"
              >
                Annuler
              </button>
            </div>
          </div>
        </dialog>

        {/* Modal de confirmation de retrait */}
        <dialog
          id="remove-modal"
          className={`modal ${showRemoveModal ? "modal-open" : ""}`}
        >
          <div className="modal-box">
            <h3 className="font-bold text-lg text-gray-900">
              Confirmer le retrait
            </h3>
            <p className="text-sm text-gray-600 py-4">
              Êtes-vous sûr de vouloir retirer {selectedDossiers.length}{" "}
              dossier(s) de ce prestataire ?
            </p>
            <div className="modal-action">
              <button
                onClick={handleRemoveDossiers}
                disabled={isRemoving}
                className="btn btn-sm bg-red-500 text-white hover:bg-red-600 border-red-500"
              >
                {isRemoving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Retrait...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Confirmer
                  </>
                )}
              </button>
              <button
                onClick={() => setShowRemoveModal(false)}
                disabled={isRemoving}
                className="btn btn-sm"
              >
                Annuler
              </button>
            </div>
          </div>
        </dialog>

        {/* Dossiers du prestataire sélectionné */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            {!item ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="bg-amber-100 rounded-full p-6 mb-4">
                  <BackpackIcon className="w-12 h-12 text-amber-600" />
                </div>
                <p className="text-sm font-medium text-gray-900 mb-1">
                  Aucun prestataire sélectionné
                </p>
                <p className="text-xs text-gray-500 text-center px-4">
                  Sélectionnez un prestataire pour voir ses dossiers attribués
                </p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="bg-amber-100 w-10 h-10 rounded-full flex items-center justify-center">
                      <BackpackIcon className="w-5 h-5 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {item.designation}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {selectedPrestataireDossiers.length} dossier(s)
                        attribué(s)
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setItem(null)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <Cross2Icon className="w-4 h-4 text-gray-500" />
                  </button>
                </div>

                {/* Barre d'actions pour sélection */}
                {selectedPrestataireDossiers.length > 0 && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-sm checkbox-warning"
                          checked={
                            selectedDossiers.length ===
                            selectedPrestataireDossiers.length
                          }
                          onChange={handleSelectAll}
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Tout sélectionner
                        </span>
                      </label>
                      {selectedDossiers.length > 0 && (
                        <span className="text-xs text-gray-500">
                          ({selectedDossiers.length} sélectionné
                          {selectedDossiers.length > 1 ? "s" : ""})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedDossiers.length > 0 && (
                        <button
                          onClick={() => setShowRemoveModal(true)}
                          className="btn btn-sm bg-red-500 text-white hover:bg-red-600 border-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                          Retirer ({selectedDossiers.length})
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className="relative flex-1 max-w-sm my-4">
                  <input
                    onChange={(e) => fetchDossiers(dossierPage, e.target.value)}
                    type="text"
                    placeholder="Rechercher ..."
                    className="input input-bordered px-3 pl-10 pr-10 w-full h-11 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                  />
                  <Search className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
                </div>

                {/* Contenu */}
                {loadingDossiers ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-500 mb-3" />
                    <p className="text-sm text-gray-600">
                      Chargement des dossiers...
                    </p>
                  </div>
                ) : selectedPrestataireDossiers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="bg-gray-100 rounded-full p-4 mb-3">
                      <Package className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-600 font-medium mb-1">
                      Aucun dossier attribué
                    </p>
                    <p className="text-xs text-gray-400 text-center px-4">
                      Ce prestataire n'a pas encore de dossiers
                    </p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto pr-2">
                    {selectedPrestataireDossiers.map((d) => (
                      <div
                        key={d.id}
                        className={`border rounded-lg p-4 hover:shadow-md transition-all ${
                          selectedDossiers.includes(d.abonnement?.id)
                            ? "border-amber-200 bg-amber-50"
                            : "border-gray-200 bg-white"
                        }`}
                      >
                        {/* Checkbox de sélection */}
                        <div className="flex items-start justify-between mb-2">
                          <input
                            type="checkbox"
                            className="checkbox checkbox-sm checkbox-warning mt-1"
                            checked={selectedDossiers.includes(
                              d.abonnement?.id,
                            )}
                            onChange={() =>
                              handleSelectDossier(d.abonnement?.id)
                            }
                          />
                        </div>

                        {/* Header du dossier */}
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-dashed border-amber-300">
                          <div className="text-sm font-semibold text-gray-900">
                            N° {d?.abonnement?.eligibilite?.numeroTicket}
                          </div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            {d?.dateRdv}
                          </div>
                        </div>

                        {/* Contenu */}
                        <div className="space-y-2">
                          <Periode item={d} />
                          <Pbo
                            pbo={d?.abonnement?.eligibilite?.pbo?.nom}
                            port={d?.brin}
                          />

                          <div className="flex items-start gap-2 pt-2">
                            <PersonIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {d?.abonnement?.client?.nomPrenoms}
                              </div>
                              <div className="text-xs text-gray-500">
                                {d?.abonnement?.client?.numero}
                              </div>
                            </div>
                          </div>

                          {d?.abonnement?.commune && (
                            <div className="flex items-center gap-1 text-xs text-gray-600 pt-1">
                              <HomeIcon className="w-3 h-3" />
                              {d?.abonnement?.commune}
                              {d?.abonnement?.quartier &&
                                `, ${d?.abonnement?.quartier}`}
                            </div>
                          )}

                          {d?.abonnement?.status && (
                            <div className="pt-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                                {d?.abonnement?.status
                                  ?.toLowerCase()
                                  .replace(/_/g, " ")}
                              </span>
                            </div>
                          )}

                          {d?.equipe && (
                            <div className="pt-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                                Équipe: {d?.equipe?.designation}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setAssignModalItem(d);
                                setMsg("");
                                document
                                  .getElementById("assign-modal-equipe")
                                  .showModal();
                              }}
                              className="btn btn-sm bg-main border-main my-3 font-mtn text-xs font-regular hover:bg-opacity-90 transition-colors"
                            >
                              {d?.equip === null ? "Attribuer" : "Réaffecter"}
                            </button>
                          </div>

                          <dialog id="edit" className="modal ">
                            <div className="modal-box w-10/12 max-w-2xl">
                              <div className="modal-action">
                                <h1 className="mr-auto text-2xl font-bold font-mtn">
                                  Modification de l'abonnement
                                </h1>
                                <form method="dialog">
                                  <button
                                    id="fermer"
                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-600 font-semibold"
                                  >
                                    ✕
                                  </button>
                                </form>
                              </div>
                              <p className="text-sm text-red-500">{message}</p>

                              <form className="my-4"></form>
                            </div>
                          </dialog>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <Pagination
                  className="mt-4 flex justify-center"
                  page={dossierPage + 1}
                  onChange={(event, newValue) => fetchDossiers(newValue - 1)}
                  count={dossierTotalPages}
                  size="small"
                  variant="outlined"
                  color="primary"
                  shape="rounded"
                  disabled={loadingDossiers}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
